import { Checkbox as ShadcnCheckbox } from "@/components/ui/checkbox";
import { cx } from "@/utils/cx";
import { useId, type ComponentProps, type ReactNode, type Ref } from "react";

export interface CheckboxBaseProps {
    size?: "sm" | "md";
    className?: string;
    isFocusVisible?: boolean;
    isSelected?: boolean;
    disabled?: boolean;
    isIndeterminate?: boolean;
}

export const CheckboxBase = ({ className, isSelected, disabled, isIndeterminate, size = "sm", isFocusVisible = false }: CheckboxBaseProps) => {
    return (
        <ShadcnCheckbox
            checked={isSelected}
            disabled={disabled}
            indeterminate={isIndeterminate}
            tabIndex={-1}
            aria-hidden="true"
            className={cx(
                size === "md" && "size-5",
                isFocusVisible && "ring-3 ring-ring/50",
                className,
            )}
        />
    );
};
CheckboxBase.displayName = "CheckboxBase";

interface CheckboxProps extends Omit<ComponentProps<typeof ShadcnCheckbox>, "checked" | "defaultChecked" | "onCheckedChange" | "disabled" | "className" | "ref"> {
    ref?: Ref<HTMLLabelElement>;
    size?: "sm" | "md";
    label?: ReactNode;
    hint?: ReactNode;
    disabled?: boolean;
    isDisabled?: boolean;
    isSelected?: boolean;
    defaultSelected?: boolean;
    onChange?: (checked: boolean) => void;
    isIndeterminate?: boolean;
    className?: string;
    checked?: boolean;
    defaultChecked?: boolean;
    onCheckedChange?: (checked: boolean) => void;
    indeterminate?: boolean;
    id?: string;
    "data-testid"?: string;
}

export const Checkbox = ({
    label,
    hint,
    size = "sm",
    className,
    disabled,
    isDisabled,
    isSelected,
    defaultSelected,
    onChange,
    isIndeterminate,
    checked,
    defaultChecked,
    onCheckedChange,
    indeterminate,
    id,
    ref,
    "data-testid": dataTestId,
    ...checkboxProps
}: CheckboxProps) => {
    const generatedId = useId();
    const checkboxId = id ?? generatedId;
    const sizes = {
        sm: {
            root: "gap-2",
            textWrapper: "",
            label: "text-sm font-medium",
            hint: "text-sm",
        },
        md: {
            root: "gap-3",
            textWrapper: "gap-0.5",
            label: "text-md font-medium",
            hint: "text-md",
        },
    };

    return (
        <label
            ref={ref}
            htmlFor={checkboxId}
            data-testid={dataTestId}
            className={cx(
                "m-0 flex items-start",
                (disabled ?? isDisabled) && "cursor-not-allowed",
                sizes[size].root,
                className,
            )}
        >
            <ShadcnCheckbox
                {...checkboxProps}
                id={checkboxId}
                checked={checked ?? isSelected}
                defaultChecked={defaultChecked ?? defaultSelected}
                onCheckedChange={onCheckedChange ?? onChange}
                disabled={disabled ?? isDisabled}
                indeterminate={indeterminate ?? isIndeterminate}
                className={cx(size === "md" && "size-5", (label || hint) && "mt-0.5")}
            />
            {(label || hint) && (
                <span className={cx("inline-flex flex-col", sizes[size].textWrapper)}>
                    {label && <span className={cx("select-none", sizes[size].label)}>{label}</span>}
                    {hint && <span className={cx("text-primary-foreground/90", sizes[size].hint)}>{hint}</span>}
                </span>
            )}
        </label>
    );
};
Checkbox.displayName = "Checkbox";
